import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const shippingFee = (subtotal) => (subtotal >= 5000 ? 0 : 199);

export default function Checkout() {
  const cart = useCart();
  const [form, setForm] = useState({ customer_name: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const placeOrder = async (e) => {
    e.preventDefault();
    if (cart.items.length === 0) return;
    setPlacing(true);
    try {
      const { data: order } = await api.post("/orders", {
        ...form,
        items: cart.items.map((i) => ({ product_id: i.product_id, qty: i.qty, size: i.size })),
      });
      const number = (process.env.REACT_APP_WHATSAPP_NUMBER || "").replace(/\D/g, "");
      const lines = [`New website order № ${order.id.slice(-6).toUpperCase()} — Hunar Designs`, ""];
      order.items.forEach((i) =>
        lines.push(`• ${i.name}${i.size ? ` (Size ${i.size})` : ""} × ${i.qty} — ₹${Number(i.price * i.qty).toLocaleString("en-IN")}`)
      );
      lines.push(
        "",
        `Total: ₹${Number(order.total).toLocaleString("en-IN")}`,
        "",
        `Name: ${order.customer_name}`,
        `Phone: ${order.phone}`,
        `Address: ${order.shipping.address}, ${order.shipping.city}${order.shipping.state ? `, ${order.shipping.state}` : ""} ${order.shipping.pincode}`
      );
      window.open(`https://wa.me/${number}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
      cart.clear();
      setPlaced(order);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  if (placed) {
    return (
      <div data-testid="order-success" className="px-6 py-24 md:px-12 lg:px-20">
        <div className="mx-auto max-w-xl">
          <CheckCircle2 className="h-12 w-12 text-olive" />
          <h1 className="mt-6 font-serif text-4xl font-light tracking-tight sm:text-5xl">
            Order placed, <span className="italic">thank you!</span>
          </h1>
          <p className="mt-4 text-base leading-relaxed text-soot">
            Your order № <span className="font-bold text-obsidian">{placed.id.slice(-6).toUpperCase()}</span> for{" "}
            <span className="font-bold text-obsidian">{inr(placed.total)}</span> is with us. We&rsquo;ve opened WhatsApp
            with your order details — just press send, and we&rsquo;ll confirm payment and delivery right there.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/shop"
              data-testid="continue-shopping-button"
              className="inline-flex items-center gap-3 rounded-full bg-obsidian px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-bone transition-colors hover:bg-olive"
            >
              Continue Shopping
            </Link>
            <a
              href={`https://wa.me/${(process.env.REACT_APP_WHATSAPP_NUMBER || "").replace(/\D/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="success-whatsapp-link"
              className="inline-flex items-center gap-2 rounded-full border border-[#1FA855] px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-[#1FA855] transition-colors hover:bg-[#1FA855] hover:text-white"
            >
              <MessageCircle className="h-4 w-4" /> Chat with Us
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="px-6 py-32" data-testid="checkout-empty">
        <p className="font-serif text-3xl italic text-soot">Your bag is empty.</p>
        <Link to="/shop" className="mt-4 inline-block text-xs font-bold uppercase tracking-[0.18em] underline underline-offset-8">
          Back to Shop
        </Link>
      </div>
    );
  }

  const total = cart.subtotal + shippingFee(cart.subtotal);

  return (
    <div data-testid="checkout-page" className="px-6 py-12 md:px-12 lg:px-20 lg:py-20">
      <div className="mx-auto max-w-[1200px]">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-soot">Almost yours</p>
        <h1 className="mt-4 font-serif text-4xl font-light tracking-tight sm:text-5xl md:text-6xl">Checkout</h1>

        <form onSubmit={placeOrder} className="mt-12 grid gap-12 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-7">
            <h2 className="font-serif text-2xl">Shipping Details</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="ship-name">Full Name</Label>
                <Input id="ship-name" data-testid="shipping-name-input" required value={form.customer_name} onChange={set("customer_name")} className="mt-1.5 bg-transparent" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="ship-phone">Phone Number</Label>
                <Input id="ship-phone" data-testid="shipping-phone-input" required minLength={8} maxLength={15} placeholder="10-digit mobile number" value={form.phone} onChange={set("phone")} className="mt-1.5 bg-transparent" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="ship-address">Address</Label>
                <Input id="ship-address" data-testid="shipping-address-input" required placeholder="House no, street, area" value={form.address} onChange={set("address")} className="mt-1.5 bg-transparent" />
              </div>
              <div>
                <Label htmlFor="ship-city">City</Label>
                <Input id="ship-city" data-testid="shipping-city-input" required value={form.city} onChange={set("city")} className="mt-1.5 bg-transparent" />
              </div>
              <div>
                <Label htmlFor="ship-state">State</Label>
                <Input id="ship-state" data-testid="shipping-state-input" value={form.state} onChange={set("state")} className="mt-1.5 bg-transparent" />
              </div>
              <div>
                <Label htmlFor="ship-pincode">Pincode</Label>
                <Input id="ship-pincode" data-testid="shipping-pincode-input" required minLength={4} maxLength={10} value={form.pincode} onChange={set("pincode")} className="mt-1.5 bg-transparent" />
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-seam p-6 md:p-8" data-testid="order-summary">
              <h2 className="font-serif text-2xl">Order Summary</h2>
              <div className="mt-6 space-y-4">
                {cart.items.map((i) => (
                  <div key={i.key} className="flex items-center gap-4">
                    <div className="h-16 w-12 shrink-0 overflow-hidden bg-linen">
                      {i.image && <img src={i.image} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-serif leading-tight">{i.name}</p>
                      <p className="text-xs text-soot">
                        {i.size ? `Size ${i.size} · ` : ""}Qty {i.qty}
                      </p>
                    </div>
                    <p className="text-sm font-semibold">{inr(i.price * i.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-2 border-t border-seam pt-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-soot">Subtotal</span>
                  <span>{inr(cart.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-soot">Shipping</span>
                  <span>{shippingFee(cart.subtotal) === 0 ? "Free" : inr(shippingFee(cart.subtotal))}</span>
                </div>
                <div className="flex justify-between border-t border-seam pt-3 text-base font-semibold">
                  <span>Total</span>
                  <span data-testid="checkout-total">{inr(total)}</span>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="place-order-button"
                disabled={placing}
                className="mt-6 h-12 w-full rounded-full bg-obsidian text-xs font-bold uppercase tracking-[0.18em] text-bone transition-colors duration-300 hover:bg-olive"
              >
                {placing ? "Placing Order…" : "Place Order"}
              </Button>
              <p className="mt-3 text-center text-xs text-soot">
                Your order goes straight to us — we confirm payment &amp; delivery on WhatsApp.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
